import React, { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'

const DropDown = ({ options, selected, placeholder, className, optionClassName, clickPayload, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState('bottom')
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      
      // Check if dropdown would go off-screen
      const rect = dropdownRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setDropdownPosition('top')
      } else {
        setDropdownPosition('bottom')
      }
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])
  
  const handleSelect = (option) => {
    if (option.onClick) {
      option.onClick(clickPayload)
    }
    if (onChange) {
      onChange(option.value)
    }
    setIsOpen(false)
  }
  
  return (
    <div className={clsx('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        className="select select-bordered select-sm w-full text-left flex items-center justify-between bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
          {selected?.label || placeholder || 'Select an option'}
        </span>
        <ChevronDownIcon className={clsx('w-4 h-4 transition-transform text-gray-500 dark:text-gray-400', isOpen && 'rotate-180')} />
      </button>
      
      {isOpen && (
        <div 
          className={clsx(
            'absolute left-0 w-full mt-1 rounded-lg shadow-lg border',
            'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600',
            dropdownPosition === 'top' ? 'bottom-full mb-1 mt-0' : 'top-full'
          )}
          style={{
            zIndex: 999999,
            maxHeight: '240px',
            overflowY: 'auto',
            minWidth: '100%',
            position: 'absolute'
          }}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={clsx(
                'w-full px-4 py-2 text-left flex items-center justify-between transition-colors',
                'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700',
                option.value === selected?.value && 'bg-blue-50 dark:bg-blue-900/20'
              )}
              onClick={() => handleSelect(option)}
            >
              <span>{option.label}</span>
              {option.value === selected?.value && (
                <CheckIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default DropDown